/** What a recording produced, with enough about it to tell a real question from a dud. */
export interface VoiceRecording {
  /** The encoded audio; its `type` is what the browser could encode. */
  blob: Blob;

  /** How long the microphone was open, in milliseconds. */
  durationMs: number;

  /**
   * The loudest moment, as RMS of the signal in 0..1. Speech is well above
   * 0.01; a microphone delivering silence stays near 0.
   */
  peakLevel: number;

  /**
   * The browser reported the microphone as muted -- a device that is present
   * but not delivering audio, such as a Bluetooth headset out of a call.
   */
  muted: boolean;
}

export interface VoiceRecorderOptions {
  /** A specific microphone (a MediaDeviceInfo.deviceId); the default one when empty. */
  deviceId?: string;

  /** Called when the recording is cut off at MAX_DURATION_MS, so the caller can finish it. */
  onLimit: () => void;

  /** Called a few times a second with the current level, 0..1, for a meter. */
  onLevel?: (level: number) => void;

  /** Called when the microphone's muted state changes while recording. */
  onMuted?: (muted: boolean) => void;
}

/** Below this peak RMS the recording is treated as having captured no sound at all. */
export const SILENCE_LEVEL = 0.01;

/**
 * Records one spoken question from the microphone.
 *
 * A thin wrapper over MediaRecorder that owns the microphone stream for the
 * duration of a recording and hands the result back as a single Blob whose
 * type is whatever the browser could encode -- Opus in WebM on Chromium and
 * Firefox, AAC in MP4 on Safari. The backend forwards it by that type.
 *
 * It also watches what the microphone actually delivers. A browser will
 * happily "record" from a device that produces no audio -- Edge picked a
 * Bluetooth headset's idle hands-free channel and sent a 1 KB file with no
 * frames in it -- and the only symptom downstream is "no speech recognised".
 * The level and the muted flag let the caller say what really happened.
 *
 * Recordings are capped: a question is seconds long, and a forgotten open
 * microphone should not turn into a multi-megabyte upload.
 */
export class VoiceRecorder {
  /** Longest recording accepted, in milliseconds. */
  static readonly MAX_DURATION_MS = 120_000;

  // Most to least preferred; the first one the browser can encode is used.
  private static readonly MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private chunks: Blob[] = [];
  private limitTimer: ReturnType<typeof setTimeout> | null = null;
  private levelTimer: ReturnType<typeof setInterval> | null = null;
  private startedAt = 0;
  private peakLevel = 0;

  /** True when this browser can capture and encode audio at all. */
  static isSupported(): boolean {
    return (
      typeof MediaRecorder !== 'undefined' && typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
    );
  }

  /**
   * The microphones the browser knows of. Labels are only filled in once the
   * site has been allowed to use a microphone; before that the list is of
   * anonymous devices and not worth showing. Chromium's synthetic "default"
   * and "communications" entries are left out -- the caller offers its own
   * default option.
   */
  static async listMicrophones(): Promise<MediaDeviceInfo[]> {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return [];
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      return devices.filter(
        (device) =>
          device.kind === 'audioinput' &&
          device.deviceId &&
          device.deviceId !== 'default' &&
          device.deviceId !== 'communications' &&
          device.label
      );
    } catch {
      return [];
    }
  }

  get recording(): boolean {
    return this.recorder?.state === 'recording';
  }

  /**
   * Asks for the microphone and starts recording. Rejects when the user
   * refuses the permission or there is no microphone -- the DOMException from
   * getUserMedia, with its `name` (NotAllowedError, NotFoundError, ...). A
   * chosen device that is no longer there falls back to the default one.
   */
  async start(options: VoiceRecorderOptions): Promise<void> {
    if (this.recorder) {
      return;
    }

    const stream = await this.openMicrophone(options.deviceId);
    const mimeType = VoiceRecorder.MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

    this.stream = stream;
    this.recorder = recorder;
    this.chunks = [];
    this.peakLevel = 0;

    recorder.ondataavailable = (event) => {
      if (event.data.size) {
        this.chunks.push(event.data);
      }
    };

    const [track] = stream.getAudioTracks();

    if (track && options.onMuted) {
      const report = () => options.onMuted?.(track.muted);
      track.onmute = report;
      track.onunmute = report;
      report();
    }

    this.watchLevel(stream, options.onLevel);

    recorder.start();
    this.startedAt = performance.now();
    this.limitTimer = setTimeout(options.onLimit, VoiceRecorder.MAX_DURATION_MS);
  }

  /**
   * Stops recording and resolves with everything captured. Resolves with an
   * empty recording when nothing was recording.
   */
  stop(): Promise<VoiceRecording> {
    const recorder = this.recorder;

    if (!recorder) {
      return Promise.resolve({ blob: new Blob(), durationMs: 0, peakLevel: 0, muted: false });
    }

    return new Promise<VoiceRecording>((resolve) => {
      const finish = () => {
        const recording: VoiceRecording = {
          blob: new Blob(this.chunks, { type: recorder.mimeType }),
          durationMs: performance.now() - this.startedAt,
          peakLevel: this.peakLevel,
          muted: this.stream?.getAudioTracks().some((track) => track.muted) ?? false,
        };
        this.release();
        resolve(recording);
      };

      if (recorder.state === 'inactive') {
        // Already stopped (e.g. the track ended); onstop will not fire again.
        finish();

        return;
      }

      recorder.onstop = finish;
      recorder.stop();
    });
  }

  /** Stops recording and throws the recording away. */
  cancel(): void {
    const recorder = this.recorder;

    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null;
      recorder.stop();
    }

    this.release();
  }

  private async openMicrophone(deviceId?: string): Promise<MediaStream> {
    if (deviceId) {
      try {
        return await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: deviceId } },
        });
      } catch (error) {
        // The remembered microphone was unplugged: the default one will do.
        // A refused permission is not something to retry.
        if (error instanceof DOMException && error.name === 'NotAllowedError') {
          throw error;
        }
      }
    }

    return navigator.mediaDevices.getUserMedia({ audio: true });
  }

  /**
   * Samples the signal a few times a second. Only the RMS is kept, for the
   * meter and for telling a silent capture apart from a quiet one afterwards.
   */
  private watchLevel(stream: MediaStream, onLevel?: (level: number) => void): void {
    if (typeof AudioContext === 'undefined') {
      return;
    }

    try {
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      context.createMediaStreamSource(stream).connect(analyser);
      // Contexts made outside a user gesture start suspended; the click that
      // started the recording counts, but resuming explicitly costs nothing.
      void context.resume();
      this.audioContext = context;

      const samples = new Float32Array(analyser.fftSize);

      this.levelTimer = setInterval(() => {
        analyser.getFloatTimeDomainData(samples);

        let sum = 0;
        for (const sample of samples) {
          sum += sample * sample;
        }

        const rms = Math.sqrt(sum / samples.length);
        this.peakLevel = Math.max(this.peakLevel, rms);
        // Speech peaks around 0.1-0.3 RMS; scaled so that fills the meter.
        onLevel?.(Math.min(1, rms * 4));
      }, 100);
    } catch {
      // No meter, no silence check -- the recording itself still works.
    }
  }

  /** Lets go of the microphone -- the browser's recording indicator goes off. */
  private release(): void {
    if (this.limitTimer) {
      clearTimeout(this.limitTimer);
      this.limitTimer = null;
    }

    if (this.levelTimer) {
      clearInterval(this.levelTimer);
      this.levelTimer = null;
    }

    void this.audioContext?.close().catch(() => undefined);
    this.audioContext = null;

    this.stream?.getTracks().forEach((track) => {
      track.onmute = null;
      track.onunmute = null;
      track.stop();
    });
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
  }
}
